import { Button, ButtonLoading } from '@tovala/component-library'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { events } from 'analytics/events'
import { ErrorCodeMessageMapCombinedAPI } from 'types/internal'
import { track } from 'utils/analytics'
import { wrapWithContactSupportTeam } from 'utils/errors'

import { useToast } from 'contexts/toast'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import ConfirmationDialog, {
  ConfirmationBody,
  ConfirmationButtons,
  ConfirmationHeader,
} from 'components/common/ConfirmationDialog'
import FormComboboxRHF from 'components/common/FormComboboxRHF'

import { Birthday, useUpdateBirthday } from '@tovala/browser-apis-combinedapi'

import { DAYS_IN_MONTH, MONTH_NAMES } from '../utils'

type Option = { label: string; value: number }

type FormValues = {
  day: Option | null
  month: Option | null
}

const MONTH_OPTIONS: Option[] = MONTH_NAMES.map((label, index) => ({
  label,
  value: index + 1,
}))

function dayOptions(month: number | undefined): Option[] {
  const max = month ? DAYS_IN_MONTH[month] : 31
  return Array.from({ length: max }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  }))
}

const UPDATE_BIRTHDAY_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    wayOut: wrapWithContactSupportTeam('If you need further assistance'),
    why: "We couldn't update your birthday due to a technical issue on our end.",
  },
}

const REMOVE_BIRTHDAY_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    wayOut: wrapWithContactSupportTeam('If you need further assistance'),
    why: "We couldn't remove your birthday due to a technical issue on our end.",
  },
}

const BirthdayForm = ({
  birthday,
  closeCollapsible,
  userID,
}: {
  birthday: Birthday | null
  closeCollapsible: () => void
  userID: number
}) => {
  const { openToast } = useToast()

  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false)

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      day: birthday
        ? { label: String(birthday.day), value: birthday.day }
        : null,
      month: birthday
        ? { label: MONTH_NAMES[birthday.month - 1], value: birthday.month }
        : null,
    },
  })

  const watchedMonth = useWatch({ control, name: 'month' })
  const watchedDay = useWatch({ control, name: 'day' })

  // Reset day when changing months would make the current day invalid (e.g., Mar 31 → Feb).
  useEffect(() => {
    if (
      watchedMonth &&
      watchedDay &&
      watchedDay.value > DAYS_IN_MONTH[watchedMonth.value]
    ) {
      setValue('day', null, { shouldValidate: false })
    }
  }, [watchedMonth, watchedDay, setValue])

  const {
    error: updateError,
    isError: hasUpdateError,
    isPending,
    mutate: updateBirthdayRecord,
  } = useUpdateBirthday({
    onSuccess: () => {
      track(events.DID_CHANGE_ACCOUNT_BIRTHDAY)

      openToast({
        heading: 'Changes Saved',
        message: 'Your birthday has been updated.',
        type: 'success',
      })

      closeCollapsible()
    },
  })

  const {
    error: removeError,
    isError: hasRemoveError,
    isPending: isRemoving,
    mutate: removeBirthdayRecord,
  } = useUpdateBirthday({
    onSuccess: () => {
      track(events.DID_CHANGE_ACCOUNT_BIRTHDAY)

      openToast({
        heading: 'Changes Saved',
        message: 'Your birthday has been removed.',
        type: 'success',
      })

      setIsConfirmingRemove(false)
      closeCollapsible()
    },
  })

  return (
    <>
      <form
        className="space-y-4"
        onSubmit={handleSubmit(({ day, month }) => {
          if (!month || !day) {
            return
          }

          updateBirthdayRecord({
            data: { day: day.value, month: month.value },
            userID,
          })
        })}
      >
        <div className="space-y-3">
          <div>
            <p className="mb-3 text-h/14_120 font-semibold md:mb-2">
              Date of Birth
            </p>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <FormComboboxRHF
                control={control}
                id="birthday-month"
                inputAriaLabel="Month"
                isClearable={false}
                name="month"
                options={MONTH_OPTIONS}
              />
              <FormComboboxRHF
                control={control}
                id="birthday-day"
                inputAriaLabel="Day"
                isClearable={false}
                name="day"
                options={dayOptions(watchedMonth?.value)}
              />
            </div>
          </div>

          <p className="text-body-sm text-grey-9">
            By providing your date of birth, you consent to our collection and
            use of this information in accordance with our{' '}
            <Link className="underline" to="/legal/privacy-policy">
              Privacy Policy
            </Link>
            .
          </p>

          {birthday && (
            <Button
              buttonStyle="link"
              onClick={() => setIsConfirmingRemove(true)}
              size="auto"
              type="button"
            >
              <span className="text-orange-1">Remove Date of Birth</span>
            </Button>
          )}
        </div>

        {hasUpdateError && (
          <div className="mt-4">
            <APIErrorDisplay
              error={updateError}
              errorCodeMessageMap={UPDATE_BIRTHDAY_ERRORS}
            />
          </div>
        )}

        <ButtonLoading
          disabled={!watchedMonth || !watchedDay}
          isLoading={isPending}
          size="large"
          type="submit"
        >
          Save
        </ButtonLoading>
      </form>

      {isConfirmingRemove && (
        <ConfirmationDialog onRequestClose={() => setIsConfirmingRemove(false)}>
          <ConfirmationHeader
            heading="Remove Date of Birth"
            onClickClose={() => setIsConfirmingRemove(false)}
          />
          <ConfirmationBody>
            <div className="space-y-4 pb-20 pt-3 text-left text-body-sm text-grey-9 md:py-12">
              <p className="p-4 text-center">
                Are you sure you want to remove your date of birth?
              </p>

              {hasRemoveError && (
                <APIErrorDisplay
                  error={removeError}
                  errorCodeMessageMap={REMOVE_BIRTHDAY_ERRORS}
                />
              )}
            </div>
          </ConfirmationBody>
          <ConfirmationButtons>
            <div className="flex justify-between">
              <Button
                buttonStyle="stroke"
                onClick={() => setIsConfirmingRemove(false)}
                size="large"
              >
                Cancel
              </Button>

              <ButtonLoading
                isLoading={isRemoving}
                onClick={() => {
                  removeBirthdayRecord({
                    data: { day: null, month: null },
                    userID,
                  })
                }}
                size="large"
              >
                Remove
              </ButtonLoading>
            </div>
          </ConfirmationButtons>
        </ConfirmationDialog>
      )}
    </>
  )
}

export default BirthdayForm
